<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soProductCategoryName"
        v-model="formData.so_product_category_name"
        :label="t('Nome da Categoria de Produto')"
        :placeholder="t('Digite o nome da categoria de produto')"
        type="text"
        required
        :validation="validateName"
        fluid
        data-testid="so-product-categories-name-input"
      />
      <small v-if="errors.so_product_category_name" class="error-message">{{ errors.so_product_category_name }}</small>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  itemId?: Id;
  isEditMode?: boolean;
}

interface ProductCategoryRow {
  so_product_category_name?: string;
  [key: string]: unknown;
}

interface ApiError extends Error {
  response?: { data?: { error?: string } };
}

interface SavePayload {
  so_product_category_name: string;
  so_product_category_id?: number;
}

const props = withDefaults(defineProps<Props>(), {
  itemId: undefined,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { getById, save } = useSOCatalog('product_categories');

// Convert itemId to number for consistency
const itemIdNumber = computed<number | null>(() => {
  if (!props.itemId) return null;
  const num = typeof props.itemId === 'string' ? parseInt(props.itemId, 10) : props.itemId;
  log('SOProductCategoriesForm', 'itemIdNumber', 'Converted ID', { original: props.itemId, converted: num });
  return num;
});

// Reactive data
const formData = reactive({
  so_product_category_name: ''
});

const errors = reactive({
  so_product_category_name: ''
});

const isLoading = ref<boolean>(false);

// Validation function for InputText component
const validateName = (): boolean => {
  return Boolean(formData.so_product_category_name) && formData.so_product_category_name.trim() !== '' && formData.so_product_category_name.length <= 100;
};

const loadForEdit = async (): Promise<void> => {
  if (!itemIdNumber.value) {
    logWarn('SOProductCategoriesForm', 'loadForEdit', 'No itemId provided');
    return;
  }

  log('SOProductCategoriesForm', 'loadForEdit', 'Loading product category for edit', itemIdNumber.value);
  isLoading.value = true;

  try {
    const item = (await getById(itemIdNumber.value)) as ProductCategoryRow | null;
    log('SOProductCategoriesForm', 'loadForEdit', 'Loaded data', item);

    if (!item) {
      throw new Error('Dados da categoria de produto não encontrados');
    }

    // Populate form
    formData.so_product_category_name = item.so_product_category_name || '';

    // Clear errors
    clearErrors();
  } catch (error) {
    const err = error as ApiError;
    logError('SOProductCategoriesForm', 'loadForEdit', 'Error loading product category', {
      itemId: itemIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar categoria de produto') + ': ' + errorMessage);
    emit('submit-error', err);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOProductCategoriesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_product_category_name || formData.so_product_category_name.trim() === '') {
    errors.so_product_category_name = t('Campo obrigatório');
    isValid = false;
  } else if (formData.so_product_category_name.length > 100) {
    errors.so_product_category_name = t('Nome muito longo') + ' (max 100)';
    isValid = false;
  }

  log('SOProductCategoriesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.so_product_category_name = '';
};

const resetForm = (): void => {
  log('SOProductCategoriesForm', 'resetForm', 'Resetting form');
  formData.so_product_category_name = '';
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('SOProductCategoriesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOProductCategoriesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload: SavePayload = {
      so_product_category_name: formData.so_product_category_name.trim()
    };

    if (props.isEditMode && itemIdNumber.value) {
      // Update existing
      payload.so_product_category_id = itemIdNumber.value;
      log('SOProductCategoriesForm', 'handleSubmit', 'Updating product category', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Categoria de produto atualizada com sucesso'));
    } else {
      // Create new
      log('SOProductCategoriesForm', 'handleSubmit', 'Creating product category', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Categoria de produto criada com sucesso'));
    }

    emit('submit-success');
    resetForm();
  } catch (error) {
    const err = error as Error;
    logError('SOProductCategoriesForm', 'handleSubmit', 'Error submitting form', err);
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar categoria de produto') + ': ' + err.message
        : t('Erro ao criar categoria de produto') + ': ' + err.message
    );
    emit('submit-error', err);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in itemId to reload data
watch(() => props.itemId, async (newId) => {
  log('SOProductCategoriesForm', 'watch', 'itemId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(() => {
  log('SOProductCategoriesForm', 'mounted', 'Component mounted', {
    itemId: props.itemId,
    itemIdNumber: itemIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && itemIdNumber.value) {
    loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
